import { Component } from './component.js';
import { debounce, onDocumentLoaded } from './utilities.js';
import { MegaMenuHoverEvent } from './events.js';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 150;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();
  #resizeObserver = null;

  connectedCallback() {
    super.connectedCallback();

    // Use passive listener for better scroll performance
    this.overflowMenu?.addEventListener(
      'pointerleave',
      () => this.#debouncedDeactivate(),
      {
        signal: this.#abortController.signal,
        passive: true,
      }
    );

    // Setup ResizeObserver for dynamic height tracking
    this.#setupResizeObserver();

    onDocumentLoaded(this.#preloadImages);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#resizeObserver?.disconnect();
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * Setup ResizeObserver to track submenu height changes
   */
  #setupResizeObserver() {
    if (typeof ResizeObserver === 'undefined') return;

    this.#resizeObserver = new ResizeObserver((entries) => {
      if (!this.#state.activeItem) return;

      // Use requestAnimationFrame to batch layout reads
      requestAnimationFrame(() => {
        const activeSubmenu = findSubmenu(this.#state.activeItem);
        if (!activeSubmenu) return;

        for (const entry of entries) {
          if (entry.target === activeSubmenu) {
            const height = entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height;
            this.style.setProperty('--submenu-height', `${height}px`);
            break;
          }
        }
      });
    });
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    this.#debouncedActivateHandler(event);
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
      // Keep previous submenu visible during close fade-out to prevent black flash
      previouslyActiveItem.setAttribute('data-animating', '');
      const cleanupDelay = this.animationDelay || 200;
      setTimeout(() => {
        previouslyActiveItem.removeAttribute('data-animating');
      }, cleanupDelay);
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    let overflowMenuHeight = this.overflowMenu?.offsetHeight ?? 0;

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    // Observe submenu for dynamic height changes
    if (this.#resizeObserver && submenu) {
      this.#resizeObserver.observe(submenu);
    }

    // Fix Layout Thrashing: Batch reads then writes using requestAnimationFrame
    requestAnimationFrame(() => {
      // Read phase - force single reflow
      const submenuHeight = submenu ? Math.max(submenu.offsetHeight, overflowMenuHeight) : 0;

      // Write phase - no layout recalc
      requestAnimationFrame(() => {
        this.style.setProperty('--submenu-height', `${submenuHeight}px`);
        this.style.setProperty('--submenu-opacity', '1');
      });
    });
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    if (this.overflowHovered) return;

    const submenu = findSubmenu(item);

    // Stop observing when closing
    if (this.#resizeObserver && submenu) {
      this.#resizeObserver.unobserve(submenu);
    }

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    // Keep data-animating alive for the full close animation duration
    // so .menu-list__submenu stays visibility:visible during the fade-out
    const cleanupDelay = this.animationDelay || 200;
    setTimeout(() => {
      item.removeAttribute('data-animating');
    }, cleanupDelay);
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
